/* eslint-disable eqeqeq */
import React from 'react';
import PropTypes from 'prop-types';
import ProgressBarChart from '../progressBar/progressBar';
import styles from './KPICards.module.css';

import FgCoverageTable from '../progressBar/fgCoverageTable';
import SegmentedProgressBar from '../progressBar/segmentedProgressBar';
import PieChartWithCenterLabel from '../Charts/PieChartWithCenterLabel';
import DividerTiles from './DividerTiles';

const getIconAndColor = (label, delta) => {
    let percentageColor;
    let percentage = "";

    if (delta < 0) {
        percentage = `▼ ${Math.abs(delta)}%`;
        percentageColor = "#FF544F";
    } else if (delta > 0) {
        percentage = `▲ ${delta}%`;
        percentageColor = "#00A86B";
    } else {
        percentage = `▲ 0%`;
        percentageColor = "#00A86B";
    }

    return { percentageColor, percentage };
};

const KpiCard = ({ count, text, delta, img, isMultipleWeek }) => {
    const { percentageColor, percentage } = getIconAndColor(text, delta);
    const renderCount = () => {
        if (typeof count === "object" && count !== null) {
            return (
                <div className={styles.weeklyContainer}>
                    {Object.entries(count).map(([week, value]) => {
                        if (week == 'Today' || week == 'Saturday' || week == 'Monday') {
                            return (
                                <div key={week} className={styles.contractWeekRow}>
                                    <span className={styles.kpiText} style={{ fontWeight: '500', fontSize: '13px' }}>{week}</span>
                                    <span className={styles.kpiText} style={{ fontWeight: '500', fontSize: '13px' }}>{value}</span>
                                </div>
                            )
                        }
                        else {
                            return (
                                <div key={week} className={styles.weekRow}>
                                    <span className={styles.kpiText} style={{ fontWeight: '500' }}>{week}</span>
                                    <span className={styles.kpiText} style={{ fontWeight: '500' }}>{value}</span>
                                </div>
                            )
                        }
                    })}
                </div>
            );
        } else {
            return <span className={styles.count}>{count}</span>;
        }
    };

    return (
        <div className={styles.kpiCard}
            style={{
                minWidth: 'auto',
                backgroundImage: `url(${img})`,
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                boxShadow: '0px 8px 10px 0px #959DA533',
                borderRadius: '5px'
            }}
        >
            <span className={styles.kpiText}>{text}</span>
                {renderCount()}
                {
                    (text === 'OR%' || text === 'Production Plan' || text === 'OR Loss') && !isMultipleWeek &&
                    < div className={styles.percentage}>
                        <span style={{ color: percentageColor }} className={styles.percent}>
                            {percentage}
                        </span>
                        <span className={styles.percentText}>vs Last Week</span>
                    </div>
                }
        </div>
    );
};

// Component for MAI / CBU Coverage data display
const MaiTable = ({ data, label }) => {

    const weeks = data && (data["MAI Total"] || data["CBU Coverage Total"])
        ? Object.keys(data["MAI Total"] || data["CBU Coverage Total"])
        : [];

    // Decide labels dynamically
    const isMAI = label?.toLowerCase().includes("mai");
    const phyLabel = isMAI ? "MAI% (Phy)" : "FG% (Phy)";
    const totalLabel = isMAI ? "MAI% (Total)" : "FG% (Total)";

    return (
        <div className={styles.maiTable}>
            <table>
                <thead>
                    <tr>
                        <th>WK</th>
                        <th>{phyLabel}</th>
                        <th>{totalLabel}</th>
                    </tr>
                </thead>
                <tbody>
                    {weeks?.map((week) => (
                        <tr key={week}>
                            <td>{week || 'Wk'}</td>
                            <td>
                                {data["MAI Physical"]?.[week] ||
                                    data["CBU Coverage Physical"]?.[week] || "0"}
                            </td>
                            <td>
                                {data["MAI Total"]?.[week] ||
                                    data["CBU Coverage Total"]?.[week] || "0"}
                            </td>
                        </tr>
                    ))}
                    {weeks.length === 0 && (
                        <tr>
                            <td>Wk</td>
                            <td>0</td>
                            <td>0</td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

// Main AlertRmpmKpi Component
const AlertRmpmKpi = ({ KPIObj, isMultipleWeek, type }) => {
    return (
        <div className={styles.cardContainer} style={{ width: '100%' }}>
            {KPIObj?.map((kpi) => {
                if (typeof kpi !== "object" || !kpi.label) return null;
                if (kpi.label1 || kpi.label2) {
                    return (
                        <div
                            key={kpi.label || kpi.label1}
                            className={styles.kpiCard}
                            style={{
                                backgroundImage: `url(${kpi.img})`,
                                backgroundSize: "cover",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "center",
                                boxShadow: '0px 8px 10px 0px #959DA533',
                                borderRadius: '5px'
                            }}
                        >
                            {kpi.label1 && (
                                <div className={styles.pairedKpiRow}>
                                    <span className={styles.kpiText}>{kpi.label1}</span>
                                    <span className={styles.pairedKpiValue}>{kpi.value1 ?? 0}</span>
                                </div>
                            )}
                            {kpi.label2 && (
                                <div className={styles.pairedKpiRow}>
                                    <span className={styles.kpiText}>{kpi.label2}</span>
                                    <span className={styles.pairedKpiValue}>{kpi.value2 ?? 0}</span>
                                </div>
                            )}
                        </div>
                    );
                }
                if (kpi.label.toLowerCase().includes("status") || kpi.label.toLowerCase().includes("predicted accuracy")) {
                    return (
                        <div key={kpi.label} className={styles.kpiCard}>
                            <ProgressBarChart label={kpi.label} data={kpi.values} type={type} />
                        </div>
                    );
                } else if (kpi.label.toLowerCase().includes("root cause loss tree")) {
                    return (
                        <div key={kpi.label} className={styles.kpiCardSpeedo}>
                            <SegmentedProgressBar data={kpi.values} />
                        </div>
                    )
                }
                else if (kpi.label.toLowerCase().includes("segmentation") || kpi.label.toLowerCase().includes("fg coverage") || kpi.label.toLowerCase().includes("fg coverage(priority)")) {
                    return (
                        <div key={kpi.label} className={styles.kpiCard}>
                            <FgCoverageTable label={kpi.label} data={kpi.values} />
                        </div>
                    )
                } else if (kpi.label.toLowerCase().includes("cbus impacted")) {
                    return (
                        <div key={kpi.label} className={styles.paikpiCard}>
                            <PieChartWithCenterLabel label={kpi.label} data={kpi.value} />
                        </div>
                    )
                } else if (kpi.label.toLowerCase().includes("wtd or % || agile or %")) {
                    return (
                        <div key={kpi.label} className={styles.paikpiCard}>
                            <DividerTiles label={kpi.label} data={kpi.value} />
                        </div>
                    )
                } else if (kpi.label.toLowerCase().includes("mai") || kpi.label.toLowerCase().includes("cbu coverage %")) {
                    return (
                        <div key={kpi.label} className={styles.kpiCard}>
                            <MaiTable data={kpi?.value} label={kpi?.label} />
                        </div>
                    )
                } else {
                    return (
                        <KpiCard
                            key={kpi.label}
                            count={kpi.value || kpi.values || 0}
                            text={kpi.label}
                            delta={kpi.delta}
                            img={kpi.img}
                            isMultipleWeek={isMultipleWeek}
                        />
                    );
                }
            })}
        </div>
    );
};

KpiCard.propTypes = {
  count: PropTypes.number,
  text: PropTypes.string,
  delta: PropTypes.number,
  img: PropTypes.string,
  isMultipleWeek: PropTypes.bool,
};

MaiTable.propTypes = {
  data: PropTypes.object,
  label: PropTypes.string,
};

AlertRmpmKpi.propTypes = {
  KPIObj: PropTypes.arrayOf(PropTypes.object),
  isMultipleWeek: PropTypes.bool,
  type: PropTypes.string,
};

export default AlertRmpmKpi;